/**
 * ╔══════════════════════════════════════════════════════╗
 * ║  Sweeties MFG — Instagram Reels Widget               ║
 * ║  assets/sweeties-reels.js                            ║
 * ╚══════════════════════════════════════════════════════╝
 *
 * Features:
 *   ✓ Instagram Graph API auto-sync (Business/Creator)
 *   ✓ Manual video upload support
 *   ✓ Shoppable product tags (lightbox overlay)
 *   ✓ Pin / hide individual videos
 *   ✓ Grid, Slider, Stories layouts
 *   ✓ Configurable muted autoplay preview (default 10s)
 *   ✓ Full video with audio in lightbox
 *   ✓ In-store view count tracking (localStorage)
 *   ✓ IG view count display (via Insights API)
 *   ✓ Touch swipe support
 *   ✓ Keyboard navigation
 *   ✓ Intersection Observer autoplay for mobile
 *
 * NOTE ON AUTOPLAY:
 *   All browsers require videos to be MUTED to autoplay.
 *   Preview on the feed card = muted + configurable duration.
 *   Lightbox = full audio, user-controlled.
 *
 * NOTE ON VIEW COUNTS:
 *   "In-store views" = tracked in localStorage (per browser/device).
 *   "IG views" = fetched from Instagram Insights API (only available
 *   to Business/Creator accounts via the Graph API).
 *   These are two separate counts displayed together.
 *
 * NOTE ON TOKEN:
 *   Long-lived tokens expire after 60 days. Set a calendar reminder
 *   to refresh via Meta Graph API Explorer monthly.
 */

(function () {
  'use strict';

  /* ─── Constants ─── */
  const STORE_VIEWS_KEY = 'sr_views_v1';
  const STORE_CONFIG_KEY = 'sr_config_v1';
  const IG_API_BASE = 'https://graph.instagram.com';

  /* ─── Utilities ─── */
  const $ = (sel, ctx = document) => ctx.querySelector(sel);
  const $$ = (sel, ctx = document) => Array.from(ctx.querySelectorAll(sel));

  function truncate(str, len) {
    if (!str) return '';
    return str.length > len ? str.slice(0, len) + '…' : str;
  }

  function formatNum(n) {
    n = parseInt(n, 10) || 0;
    if (n >= 1_000_000) return (n / 1_000_000).toFixed(1) + 'M';
    if (n >= 1_000) return (n / 1_000).toFixed(1) + 'K';
    return n.toLocaleString();
  }

  function loadJSON(key, fallback) {
    try {
      const raw = localStorage.getItem(key);
      return raw ? JSON.parse(raw) : fallback;
    } catch {
      return fallback;
    }
  }

  function saveJSON(key, value) {
    try {
      localStorage.setItem(key, JSON.stringify(value));
    } catch (e) {
      console.warn('[SweetiesReels] localStorage write failed:', e);
    }
  }

  /* ─── View count helpers ─── */
  function getViewCount(videoId) {
    const views = loadJSON(STORE_VIEWS_KEY, {});
    return views[videoId] || 0;
  }

  function incrementViewCount(videoId) {
    const views = loadJSON(STORE_VIEWS_KEY, {});
    views[videoId] = (views[videoId] || 0) + 1;
    saveJSON(STORE_VIEWS_KEY, views);
    return views[videoId];
  }

  /* ─── Saved per-video config (pin/hide/product tags) ─── */
  function loadVideoConfig() {
    return loadJSON(STORE_CONFIG_KEY, {});
  }

  /* ═══════════════════════════════════════════════════════
     Main class
  ═══════════════════════════════════════════════════════ */
  class SweetiesReels {
    constructor(sectionEl) {
      this.section = sectionEl;
      this.id = sectionEl.dataset.sectionId || Math.random().toString(36).slice(2);

      /* Read config from data attributes */
      this.config = {
        accessToken: (sectionEl.dataset.accessToken || '').trim(),
        layout: sectionEl.dataset.layout || 'grid',
        columns: parseInt(sectionEl.dataset.columns, 10) || 3,
        previewDuration: parseInt(sectionEl.dataset.previewDuration, 10) || 10,
        showProductTags: sectionEl.dataset.showProductTags !== 'false',
        showViewCounts: sectionEl.dataset.showViewCounts !== 'false',
        autoplayHover: sectionEl.dataset.autoplayHover !== 'false',
        autoplayScroll: sectionEl.dataset.autoplayScroll === 'true',
      };

      /* Elements */
      this.feedEl = $(`#reels-feed-${this.id}`, sectionEl) || sectionEl.querySelector('.sweeties-reels__feed');
      this.lightbox = $('#reels-lightbox');
      this.lbVideo = $('#reels-lb-video');
      this.lbTags = $('#reels-lb-tags');
      this.lbProducts = $('#reels-lb-products');
      this.lbCaption = $('#reels-lb-caption');
      this.lbStats = $('#reels-lb-stats');
      this.lbProgressBar = $('#reels-lb-progress-bar');

      /* State */
      this.videos = [];
      this.currentIndex = 0;
      this.savedConfig = loadVideoConfig();
      this._progressRAF = null;
      this._touchStartX = 0;

      this._init();
    }

    /* ─── Initialization ─── */
    async _init() {
      try {
        const [igVideos, manualVideos] = await Promise.allSettled([
          this._fetchIGVideos(),
          this._loadManualVideos(),
        ]);

        const ig = igVideos.status === 'fulfilled' ? igVideos.value : [];
        const manual = manualVideos.status === 'fulfilled' ? manualVideos.value : [];

        this.videos = this._mergeVideos(ig, manual);
        this._render(this.config.layout);
        this._setupLayoutToggle();
        this._setupLightbox();
        this._setupSwipe();
      } catch (err) {
        console.error('[SweetiesReels] Init error:', err);
        this._showError('Something went wrong loading reels.');
      }
    }

    /* ─── Instagram Graph API fetch ─── */
    async _fetchIGVideos() {
      if (!this.config.accessToken) return [];

      const fields = 'id,caption,media_type,media_url,thumbnail_url,timestamp,permalink';
      const url = `${IG_API_BASE}/me/media?fields=${fields}&limit=50&access_token=${encodeURIComponent(this.config.accessToken)}`;

      let res;
      try {
        res = await fetch(url);
      } catch (e) {
        console.warn('[SweetiesReels] Network error fetching IG API:', e.message);
        return [];
      }

      const data = await res.json();

      if (data.error) {
        console.warn('[SweetiesReels] IG API error:', data.error.message);
        return [];
      }

      /* Filter to VIDEO / REELS only */
      const reels = (data.data || []).filter(
        m => m.media_type === 'VIDEO' || m.media_type === 'REELS'
      );

      return reels.map(r => ({
        id: `ig_${r.id}`,
        rawId: r.id,
        source: 'instagram',
        videoUrl: r.media_url,
        thumbnail: r.thumbnail_url || '',
        caption: r.caption || '',
        timestamp: r.timestamp || '',
        permalink: r.permalink || '',
        igViews: null, /* fetched lazily when lightbox opens */
      }));
    }

    /* Fetch IG insights (video_views) for a single media — called lazily */
    async _fetchIGViews(rawId) {
      if (!this.config.accessToken || !rawId) return null;

      try {
        const url = `${IG_API_BASE}/${rawId}/insights?metric=video_views&access_token=${encodeURIComponent(this.config.accessToken)}`;
        const res = await fetch(url);
        const data = await res.json();
        const metric = (data.data || []).find(m => m.name === 'video_views');
        return metric ? metric.values?.[0]?.value ?? metric.value ?? null : null;
      } catch {
        return null;
      }
    }

    /* ─── Manual videos from data island ─── */
    _loadManualVideos() {
      const el = $(`#reels-manual-data-${this.id}`) || this.section.querySelector('[data-manual-videos]');
      if (!el) return [];
      try {
        const data = JSON.parse(el.textContent || '[]');
        return data.map(v => ({ ...v, id: v.id || `manual_${Math.random().toString(36).slice(2)}`, source: 'manual' }));
      } catch (e) {
        console.warn('[SweetiesReels] Failed to parse manual videos JSON:', e.message);
        return [];
      }
    }

    /* ─── Merge + apply saved config ─── */
    _mergeVideos(igVideos, manualVideos) {
      const all = [...igVideos, ...manualVideos].map(v => {
        const saved = this.savedConfig[v.id] || {};
        return {
          ...v,
          pinned: saved.pinned || false,
          hidden: saved.hidden || false,
          productTags: saved.productTags || v.productTags || [],
        };
      });

      return all
        .filter(v => !v.hidden)
        .sort((a, b) => {
          /* Pinned first */
          if (a.pinned !== b.pinned) return a.pinned ? -1 : 1;
          /* Then by timestamp descending */
          if (a.timestamp && b.timestamp) {
            return new Date(b.timestamp) - new Date(a.timestamp);
          }
          return 0;
        });
    }

    /* ═══════════════════════════════════════════════════════
       Rendering
    ═══════════════════════════════════════════════════════ */
    _render(layout) {
      this.config.layout = layout;
      this.feedEl.className = `sweeties-reels__feed sweeties-reels__feed--${layout}`;
      this.feedEl.innerHTML = '';

      if (!this.videos.length) {
        this._showEmpty();
        return;
      }

      if (layout === 'stories') this._renderStories();
      else if (layout === 'slider') this._renderSlider();
      else this._renderGrid();
      if (this.config.autoplayScroll) this._setupScrollAutoplay();
    }

    /* ── Grid ── */
    _renderGrid() {
      this.feedEl.style.setProperty('--reels-columns', this.config.columns);
      this.videos.forEach((video, i) => {
        const card = this._createCard(video, i);
        card.style.animationDelay = `${i * 40}ms`;
        this.feedEl.appendChild(card);
      });
      if (this.config.autoplayScroll) this._setupScrollAutoplay();
    }

    /* ── Slider ── */
    _renderSlider() {
      const track = document.createElement('div');
      track.className = 'sweeties-reels__slider-track';

      this.videos.forEach((video, i) => {
        const card = this._createCard(video, i);
        card.style.animationDelay = `${i * 30}ms`;
        track.appendChild(card);
      });

      const prev = document.createElement('button');
      prev.className = 'sweeties-reels__slider-btn sweeties-reels__slider-btn--prev';
      prev.innerHTML = '&#8249;';
      prev.setAttribute('aria-label', 'Previous');

      const next = document.createElement('button');
      next.className = 'sweeties-reels__slider-btn sweeties-reels__slider-btn--next';
      next.innerHTML = '&#8250;';
      next.setAttribute('aria-label', 'Next');

      const getCardWidth = () => {
        const c = track.querySelector('.sweeties-reels__card');
        return c ? c.offsetWidth + 10 : 210;
      };

      prev.addEventListener('click', () => {
        track.scrollBy({ left: -getCardWidth() * 2, behavior: 'smooth' });
      });
      next.addEventListener('click', () => {
        track.scrollBy({ left: getCardWidth() * 2, behavior: 'smooth' });
      });

      this.feedEl.appendChild(prev);
      this.feedEl.appendChild(track);
      this.feedEl.appendChild(next);
    }

    /* ── Stories ── */
    _renderStories() {
      const track = document.createElement('div');
      track.className = 'sweeties-reels__stories-track';

      this.videos.forEach((video, i) => {
        const bubble = document.createElement('div');
        bubble.className = `sweeties-reels__story-bubble${video.pinned ? ' sweeties-reels__story-bubble--pinned' : ''}`;
        bubble.dataset.index = i;

        bubble.innerHTML = `
          <div class="sweeties-reels__story-ring">
            <div class="sweeties-reels__story-inner">
              ${video.thumbnail
                ? `<img src="${video.thumbnail}" alt="${truncate(video.caption || 'Reel', 24)}" loading="lazy">`
                : '<div style="width:100%;height:100%;background:#222;"></div>'
              }
              <video
                class="sweeties-reels__story-preview"
                src="${video.videoUrl}"
                muted playsinline preload="none" loop
              ></video>
            </div>
          </div>
          <span class="sweeties-reels__story-label">${truncate(video.caption || 'Reel', 12)}</span>
        `;

        this._attachPreview(bubble, bubble.querySelector('video'));
        bubble.addEventListener('click', () => this._openLightbox(i));
        track.appendChild(bubble);
      });

      this.feedEl.appendChild(track);
    }

    /* ─── Card factory ─── */
    _createCard(video, index) {
      const card = document.createElement('div');
      card.className = `sweeties-reels__card${video.pinned ? ' sweeties-reels__card--pinned' : ''}`;
      card.dataset.index = index;
      card.dataset.videoId = video.id;
      card.setAttribute('role', 'button');
      card.setAttribute('tabindex', '0');
      card.setAttribute('aria-label', `Watch reel${video.caption ? ': ' + truncate(video.caption, 40) : ''}`);

      const storeViews = getViewCount(video.id);

      card.innerHTML = `
        <div class="sweeties-reels__preview-wrap">
          <video
            class="sweeties-reels__preview-video"
            src="${video.videoUrl}"
            ${video.thumbnail ? `poster="${video.thumbnail}"` : ''}
            muted
            playsinline
            preload="metadata"
            loop
            tabindex="-1"
          ></video>
          <div class="sweeties-reels__card-overlay" aria-hidden="true">
            <div class="sweeties-reels__play-icon">&#9654;</div>
            ${video.pinned ? '<div class="sweeties-reels__pin-badge">📌 Pinned</div>' : ''}
            ${video.productTags?.length ? `<div class="sweeties-reels__tag-badge">🛒 ${video.productTags.length} tagged</div>` : ''}
            ${this.config.showViewCounts && storeViews > 0 ? `<div class="sweeties-reels__views-badge">👁 ${formatNum(storeViews)}</div>` : ''}
          </div>
        </div>
        ${video.caption ? `<p class="sweeties-reels__card-caption">${truncate(video.caption, 80)}</p>` : ''}
      `;

      const videoEl = card.querySelector('video');
      this._attachPreview(card, videoEl);

      card.addEventListener('click', () => this._openLightbox(index));
      card.addEventListener('keydown', e => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          this._openLightbox(index);
        }
      });

      return card;
    }

    /* ─── Muted preview on hover (configurable duration) ─── */
    _attachPreview(trigger, videoEl) {
      if (!this.config.autoplayHover || !videoEl) return;

      let timer = null;

      const startPreview = () => {
        videoEl.currentTime = 0;
        videoEl.play().catch(() => {});
        timer = setTimeout(() => {
          videoEl.pause();
          videoEl.currentTime = 0;
        }, this.config.previewDuration * 1000);
      };

      const stopPreview = () => {
        clearTimeout(timer);
        videoEl.pause();
        videoEl.currentTime = 0;
      };

      trigger.addEventListener('mouseenter', startPreview);
      trigger.addEventListener('mouseleave', stopPreview);

      /* Touch: tap once to preview, tap again to open */
      let tapped = false;
      trigger.addEventListener('touchstart', e => {
        if (!tapped) {
          e.preventDefault();
          tapped = true;
          startPreview();
          setTimeout(() => { tapped = false; stopPreview(); }, this.config.previewDuration * 1000 + 200);
        }
      }, { passive: false });
    }

/* ─── Intersection Observer autoplay (scroll-into-view) ─── */
_setupScrollAutoplay() {
  if (!('IntersectionObserver' in window)) return;

  const obs = new IntersectionObserver((entries) => {
    entries.forEach(entry => {
      const videoEl = entry.target.querySelector('.sweeties-reels__preview-video');
      if (!videoEl) return;

      if (entry.isIntersecting) {
        videoEl.currentTime = 0;
        videoEl.play().catch(() => {});
      } else {
        videoEl.pause();
        videoEl.currentTime = 0;
      }
    });
  }, { threshold: 0.3 });

  $$('.sweeties-reels__card', this.feedEl).forEach(card => obs.observe(card));
}
    /* ═══════════════════════════════════════════════════════
       Lightbox
    ═══════════════════════════════════════════════════════ */
    _setupLightbox() {
      if (!this.lightbox) return;

      /* Close handlers */
      $$('[data-close-lightbox]', this.lightbox).forEach(el => {
        el.addEventListener('click', () => this._closeLightbox());
      });

      /* Nav */
      const prev = this.lightbox.querySelector('.sweeties-reels__lb-nav--prev');
      const next = this.lightbox.querySelector('.sweeties-reels__lb-nav--next');
      if (prev) prev.addEventListener('click', () => this._navigate(-1));
      if (next) next.addEventListener('click', () => this._navigate(1));

      /* Keyboard */
      document.addEventListener('keydown', e => {
        if (this.lightbox.getAttribute('aria-hidden') === 'true') return;
        if (e.key === 'Escape') this._closeLightbox();
        if (e.key === 'ArrowLeft') this._navigate(-1);
        if (e.key === 'ArrowRight') this._navigate(1);
      });

      /* Progress bar */
      if (this.lbVideo) {
        this.lbVideo.addEventListener('timeupdate', () => {
          if (!this.lbVideo.duration || !this.lbProgressBar) return;
          const pct = (this.lbVideo.currentTime / this.lbVideo.duration) * 100;
          this.lbProgressBar.style.width = `${pct}%`;
        });
      }
    }

    _openLightbox(index) {
      if (!this.lightbox) return;
      this.currentIndex = index;
      this._loadLightboxVideo(index);
      this.lightbox.setAttribute('aria-hidden', 'false');
      document.body.style.overflow = 'hidden';

      /* Pause all feed previews */
      $$('video', this.feedEl).forEach(v => v.pause());
    }

    _closeLightbox() {
      if (!this.lightbox) return;
      if (this.lbVideo) {
        this.lbVideo.pause();
        this.lbVideo.src = '';
      }
      if (this.lbProgressBar) this.lbProgressBar.style.width = '0%';
      this.lightbox.setAttribute('aria-hidden', 'true');
      document.body.style.overflow = '';
    }

    _navigate(dir) {
      this.currentIndex = (this.currentIndex + dir + this.videos.length) % this.videos.length;
      this._loadLightboxVideo(this.currentIndex);
    }

    async _loadLightboxVideo(index) {
      const video = this.videos[index];
      if (!video || !this.lbVideo) return;

      /* Track in-store view */
      const storeViews = incrementViewCount(video.id);
      this._updateCardViewBadge(video.id, storeViews);

      /* Set video */
      this.lbVideo.src = video.videoUrl;
      this.lbVideo.muted = false;
      this.lbVideo.load();
      this.lbVideo.play().catch(() => {});

      /* Caption */
      if (this.lbCaption) this.lbCaption.textContent = video.caption || '';

      /* Stats */
      if (this.lbStats) {
        this.lbStats.innerHTML = `
          <span>Store views: <strong>${formatNum(storeViews)}</strong></span>
          ${video.source === 'instagram' ? `<span id="lb-ig-views-${this.id}" style="opacity:0.5;">IG: loading…</span>` : ''}
        `;

        /* Lazy-fetch IG views */
        if (video.source === 'instagram' && video.rawId) {
          this._fetchIGViews(video.rawId).then(count => {
            const el = $(`#lb-ig-views-${this.id}`);
            if (el) {
              if (count !== null) {
                el.style.opacity = '';
                el.innerHTML = `IG views: <strong>${formatNum(count)}</strong>`;
              } else {
                el.textContent = '';
              }
            }
          });
        }
      }

      /* Product tags */
      this._renderProductTags(video);
      this._renderProductRow(video);
    }

    _updateCardViewBadge(videoId, count) {
      if (!this.config.showViewCounts) return;
      const card = this.feedEl.querySelector(`[data-video-id="${videoId}"]`);
      if (!card) return;

      let badge = card.querySelector('.sweeties-reels__views-badge');
      if (!badge) {
        badge = document.createElement('div');
        badge.className = 'sweeties-reels__views-badge';
        card.querySelector('.sweeties-reels__card-overlay')?.appendChild(badge);
      }
      badge.textContent = `👁 ${formatNum(count)}`;
    }

    /* ─── Product tags overlay on video ─── */
    _renderProductTags(video) {
      if (!this.lbTags || !this.config.showProductTags) return;
      this.lbTags.innerHTML = '';

      (video.productTags || []).forEach(tag => {
        const el = document.createElement('a');
        el.className = 'sweeties-reels__product-tag';
        el.href = tag.url || '#';
        el.target = '_blank';
        el.rel = 'noopener';
        el.style.left = `${tag.x ?? 50}%`;
        el.style.top = `${tag.y ?? 50}%`;

        el.innerHTML = `
          <span class="sweeties-reels__tag-dot"></span>
          <span class="sweeties-reels__tag-bubble">
            ${tag.image ? `<img src="${tag.image}" alt="${tag.title || ''}">` : ''}
            <span>${tag.title || ''}</span>
            ${tag.price ? `<strong>${tag.price}</strong>` : ''}
          </span>
        `;
        this.lbTags.appendChild(el);
      });
    }

    /* ─── Product row below video ─── */
    _renderProductRow(video) {
      if (!this.lbProducts || !this.config.showProductTags) return;
      this.lbProducts.innerHTML = '';

      (video.productTags || []).forEach(tag => {
        if (!tag.url) return;
        const el = document.createElement('a');
        el.className = 'sweeties-reels__lb-product-card';
        el.href = tag.url;
        el.target = '_blank';
        el.rel = 'noopener';
        el.innerHTML = `
          ${tag.image ? `<img src="${tag.image}" alt="${tag.title || ''}">` : ''}
          <div class="sweeties-reels__lb-product-info">
            <span class="sweeties-reels__lb-product-title">${tag.title || 'View Product'}</span>
            ${tag.price ? `<span class="sweeties-reels__lb-product-price">${tag.price}</span>` : ''}
          </div>
        `;
        this.lbProducts.appendChild(el);
      });
    }

    /* ─── Touch swipe in lightbox ─── */
    _setupSwipe() {
      if (!this.lightbox) return;

      this.lightbox.addEventListener('touchstart', e => {
        this._touchStartX = e.changedTouches[0].screenX;
      }, { passive: true });

      this.lightbox.addEventListener('touchend', e => {
        const dx = e.changedTouches[0].screenX - this._touchStartX;
        if (Math.abs(dx) > 50) this._navigate(dx < 0 ? 1 : -1);
      }, { passive: true });
    }

    /* ─── Layout toggle ─── */
    _setupLayoutToggle() {
      const toggle = this.section.querySelector('.sweeties-reels__layout-toggle');
      if (!toggle) return;

      $$('button', toggle).forEach(btn => {
        btn.addEventListener('click', () => {
          $$('button', toggle).forEach(b => {
            b.classList.remove('active');
            b.setAttribute('aria-pressed', 'false');
          });
          btn.classList.add('active');
          btn.setAttribute('aria-pressed', 'true');
          this._render(btn.dataset.layout);
        });
      });
    }

    /* ─── Error / empty states ─── */
    _showEmpty() {
      this.feedEl.innerHTML = `
        <div class="sweeties-reels__empty">
          No reels found.<br>
          Add your Instagram token in <strong>Theme Editor → Instagram Reels Feed → Long-Lived Access Token</strong>,
          or add manual videos via the admin panel.
        </div>
      `;
    }

    _showError(msg) {
      this.feedEl.innerHTML = `<div class="sweeties-reels__empty" style="color:#e07070;">${msg}</div>`;
    }
  }

  /* ═══════════════════════════════════════════════════════
     Auto-initialize
  ═══════════════════════════════════════════════════════ */
  function initAll() {
    $$('.sweeties-reels').forEach(el => {
      if (!el._sweetiesReelsInit) {
        el._sweetiesReelsInit = new SweetiesReels(el);
      }
    });
  }

  /* DOM ready */
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  /* Shopify Theme Editor re-init */
  document.addEventListener('shopify:section:load', e => {
    $$('.sweeties-reels', e.target).forEach(el => {
      el._sweetiesReelsInit = new SweetiesReels(el);
    });
  });

  document.addEventListener('shopify:section:reorder', initAll);

  /* Expose for external use (admin panel etc.) */
  window.SweetiesReels = SweetiesReels;
  window.SweetiesReelsViewCounts = {
    get: getViewCount,
    getAll: () => loadJSON(STORE_VIEWS_KEY, {}),
    increment: incrementViewCount,
  };
  window.SweetiesReelsVideoConfig = {
    load: loadVideoConfig,
    save: saveJSON.bind(null, STORE_CONFIG_KEY),
  };

})();